import React from 'react';
import {useIntl} from 'react-intl';
import {graphql, useFragment} from 'react-relay';
import Styled from 'styled-components';
import {Detail, FlexColumn, FlexRow, Modal, SearchField, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
import {InsertableTemplate, InsertTemplateListItem} from './InsertTemplateListItem';
import type {InsertTemplateList_project$key} from './__generated__/InsertTemplateList_project.graphql';

const SEARCH_WIDTH = '280px';

// The templates sit in one bordered panel that scrolls on its own, so the search and the footer stay in view.
const ListPanel = Styled.div`
	border: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
	border-radius: 4px;
	max-height: 420px;
	overflow-y: auto;
`;

export const InsertTemplateListQuery = graphql`
	query InsertTemplateListQuery($projectId: ID) {
		viewer {
			actualPersonId
			component(name: "insert_baseline_template_modal")
			project(internalId: $projectId) {
				...InsertTemplateList_project
			}
		}
	}
`;

type ContentProps = {
	project: InsertTemplateList_project$key | null;
	query: string;
	isAdmin: boolean;
	projectUsesDeliverables: boolean;
	onQueryChange: (query: string) => void;
	onPreview: (template: InsertableTemplate) => void;
	onInsert: (template: InsertableTemplate) => void;
	onManage: () => void;
	onCancel: () => void;
};

export const InsertTemplateListContent = ({
	project,
	query,
	isAdmin,
	projectUsesDeliverables,
	onQueryChange,
	onPreview,
	onInsert,
	onManage,
	onCancel,
}: ContentProps): JSX.Element => {
	const intl = useIntl();
	const data = useFragment(
		graphql`
			fragment InsertTemplateList_project on ProjectType {
				baselineTemplatesForInsert {
					id
					name
					description
					phaseCount
					totalMinutes
					roleCount
					durationDays
					includeDeliverables
					deliverableCount
				}
			}
		`,
		project
	);
	const templates = (data?.baselineTemplatesForInsert ?? []).filter((t): t is InsertableTemplate => t !== null);
	const wanted = query.trim().toLowerCase();
	const visible = wanted ? templates.filter(t => (t.name ?? '').toLowerCase().includes(wanted)) : templates;

	const emptyMessage =
		templates.length === 0
			? intl.formatMessage({
					id: isAdmin ? 'baseline_templates.insert.empty_admin' : 'baseline_templates.insert.empty_non_admin',
			  })
			: intl.formatMessage({id: 'baseline_templates.insert.no_match'}, {query: query.trim()});

	return (
		<Modal size={Modal.SIZE.LARGE} onCloseModal={onCancel}>
			<Modal.Title>{intl.formatMessage({id: 'baseline_templates.insert.title'})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap="l">
					<FlexRow gap="l" alignItems="center">
						<SearchField
							value={query}
							onChange={onQueryChange}
							width={SEARCH_WIDTH}
							placeholder={intl.formatMessage({id: 'baseline_templates.insert.search'})}
							data-cy="insert-baseline-template-search"
						/>
						{templates.length > 0 ? (
							<Detail color="auxiliary" data-cy="insert-baseline-template-count">
								{intl.formatMessage({id: 'baseline_templates.insert.count'}, {count: visible.length})}
							</Detail>
						) : null}
					</FlexRow>
					{visible.length === 0 ? (
						<Text data-cy="insert-baseline-template-empty">{emptyMessage}</Text>
					) : (
						<ListPanel data-cy="insert-baseline-template-list">
							{visible.map(template => (
								<InsertTemplateListItem
									key={template.id}
									template={template}
									projectUsesDeliverables={projectUsesDeliverables}
									onPreview={onPreview}
									onInsert={onInsert}
								/>
							))}
						</ListPanel>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				{isAdmin ? (
					<Modal.TertiaryFooterButton onPress={onManage} dataCy="insert-baseline-template-manage">
						{intl.formatMessage({id: 'baseline_templates.insert.manage'})}
					</Modal.TertiaryFooterButton>
				) : null}
				<Modal.SecondaryFooterButton onPress={onCancel} dataCy="insert-baseline-template-cancel">
					{intl.formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

type Props = Omit<ContentProps, 'project'> & {projectId: string};

/** Loads the list through the shared query boundary, so errors, logged-out and access handling match the app. */
export const InsertTemplateList = ({projectId, ...props}: Props): JSX.Element => (
	<ForecastQueryRenderer
		key="query-render-insert-baseline-template-list"
		query={InsertTemplateListQuery}
		modal={true}
		variables={{projectId}}
		render={(relayProps: {viewer: {project: InsertTemplateList_project$key | null}}) => (
			<InsertTemplateListContent project={relayProps.viewer.project} {...props} />
		)}
	/>
);
